import { AnimationSequence, motion, useAnimate } from 'framer-motion';
import { PropsWithChildren, useContext, useEffect } from 'react';
import { easeOutExpo, easeInOutExpo } from '@/utils/easing-functions';
import { styled } from '@/styles/stitches.config';
import { getThemeVariants, ThemeContext, Themes } from '../microstory/theme';

const restSpeed = 0.001;
const restDelta = 0.001;

const MotionSpan = styled(motion.span, {
  display: 'inline-block',
  paddingBlockStart: '0.14em',
  paddingBlockEnd: '0.28em',
  paddingInline: '0.5em',
  fontWeight: '$semibold',
  lineHeight: '$1',
  whiteSpace: 'nowrap',
  borderRadius: '1em',
  boxDecorationBreak: 'clone',
  '-webkit-box-decoration-break': 'clone',
  backgroundColor: '$unicef',
  variants: {
    theme: getThemeVariants({
      [Themes.sky]: {
        backgroundColor: 'var(--color-tertiary)',
      },
    }),
  },
  '[lang=bn] &, [lang=hi] &, [lang=ta] &': {
    padding: '0.28em 0.5em 0.14em',
  },
});

interface AnimatedAnswerTypeProps extends PropsWithChildren {
  className?: string;
  triggerAnimation?: number;
  index?: number;
  position?: number;
}

/**
 * This component is used for a portion of the story answer that lives on a single line (each line has it's own <AnimatedAnswerType/>)
 */
export const AnimatedAnswerType = ({
  children,
  triggerAnimation = 0,
  index = 0,
  position = 0.5,
}: AnimatedAnswerTypeProps) => {
  const theme = useContext(ThemeContext);
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const startDelay = 0.7 + index * 0.1;

    const sequence = triggerAnimation
      ? [
          [
            scope.current,
            { '--roboto-flex-xtra': 600 },
            { from: 468, type: 'tween', ease: easeOutExpo, duration: 1, delay: startDelay + 0.35 },
          ],
          [
            scope.current,
            { scaleX: 2.25 },
            { type: 'tween', ease: easeInOutExpo, delay: startDelay, duration: 0.7, at: '<' },
          ],
          [
            scope.current,
            { scaleY: 1.5 },
            { type: 'tween', ease: easeInOutExpo, delay: startDelay, duration: 0.7, at: '<' },
          ],

          [
            scope.current,
            { scaleX: 1 },
            { type: 'spring', damping: 14, stiffness: 150, restSpeed, restDelta, at: '+0.2' },
          ],
          [
            scope.current,
            { scaleY: 1 },
            {
              type: 'spring',
              damping: 14,
              stiffness: 270,
              restSpeed,
              restDelta,
              at: '<',
            },
          ],
          [
            scope.current,
            { '--roboto-flex-xtra': 468 },
            {
              type: 'tween',
              ease: 'easeOut',
              duration: 1,
              at: '<',
            },
          ],
        ]
      : [];

    animate(sequence as AnimationSequence);
  }, [triggerAnimation, animate, index, scope]);

  return (
    <MotionSpan
      ref={scope}
      initial={{
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        '--roboto-flex-xtra': 468,
        transformOrigin: `50% ${(1 - position) * 100 + '%'}`,
      }}
      theme={theme}
    >
      {children}
    </MotionSpan>
  );
};
